import type { Subscription } from '../auth/AuthContext';

/**
 * Billing Service
 *
 * Handles Stripe Checkout and Billing Portal API calls.
 * Reuses the same Sanctum/CSRF pattern as AuthContext.
 */

// CSRF helpers (same as AuthContext / chopService)
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function ensureCsrfCookie(): Promise<void> {
  if (getCsrfToken()) return;
  await fetch('/sanctum/csrf-cookie', {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });
}

async function postJson(path: string, body?: Record<string, unknown>): Promise<Response> {
  await ensureCsrfCookie();
  const csrfToken = getCsrfToken();
  return fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });
}

/**
 * Create a Stripe Checkout session and return the Checkout URL.
 * Caller should redirect to the returned URL.
 *
 * Throws with `ALREADY_SUBSCRIBED` if 409 (user has active subscription).
 */
export async function createCheckout(
  plan: 'pro',
  interval: 'monthly' | 'yearly'
): Promise<{ url: string; redirect?: string }> {
  const resp = await postJson('/api/checkout', { plan, interval });

  if (resp.status === 409) {
    const data = await resp.json().catch(() => ({}));
    // Backend says "already subscribed, use portal"
    return { url: data.url || '', redirect: 'portal' };
  }

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    // Typed causes become sentinels (see util/errorHints); anything else keeps its message for
    // the [dev] diagnostic but is never shown to a user verbatim.
    if (data?.error === 'billing_not_configured') throw new Error('BILLING_NOT_CONFIGURED');
    throw new Error(data?.message || 'Checkout failed');
  }

  return resp.json();
}

/**
 * Verify a Stripe Checkout session and sync the subscription locally.
 * Handles the webhook race condition — if the webhook hasn't processed yet,
 * this endpoint retrieves the session from Stripe and creates the subscription record.
 */
export async function verifyCheckout(sessionId: string): Promise<{ synced: boolean }> {
  const resp = await postJson('/api/billing/verify-checkout', { session_id: sessionId });

  if (!resp.ok) {
    return { synced: false };
  }

  return resp.json();
}

/**
 * Create a Stripe Customer Portal session and return the portal URL.
 * Caller should redirect to the returned URL.
 */
export async function openBillingPortal(): Promise<string> {
  const resp = await postJson('/api/billing-portal');

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    // Same contract as createCheckout: typed causes become sentinels so util/errorHints can say
    // something useful. Without this the caller only ever learned "it failed", and told the user
    // to try again — advice that cannot help with either of these two causes.
    if (data?.error === 'billing_not_configured') throw new Error('BILLING_NOT_CONFIGURED');
    if (data?.error === 'no_billing_account') throw new Error('NO_BILLING_ACCOUNT');
    throw new Error(data?.message || 'Could not open billing portal');
  }

  const data = await resp.json();
  return data.url;
}

/**
 * The signed-in customer's subscription, checked against Stripe by the server.
 *
 * The server throttles its own Stripe reads, so this is safe to call on every settings page view.
 * Pass `refresh` once after returning from the billing portal to force a (cooldown-limited) check.
 */
export async function fetchSubscription(refresh = false): Promise<Subscription | null> {
  const resp = await fetch(`/api/billing/subscription${refresh ? '?refresh=1' : ''}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  if (!resp.ok) {
    throw new Error('Could not load subscription');
  }

  const data = await resp.json();
  return data.subscription ?? null;
}
